import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash2, Layers, ChevronUp, ChevronDown, Palette } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/store/AppContext";
import { Card, PageHeader, EmptyState } from "@/components/ui-kit";
import { SAGE_PALETTE } from "@/lib/types";

const Roles: React.FC = () => {
  const { data, addRole, removeRole, moveRole, setRoleColor } = useApp();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [color, setColor] = useState(SAGE_PALETTE[0]);
  const [colorEditId, setColorEditId] = useState<string | null>(null);

  const create = () => {
    if (!name.trim()) return;
    if (data.roles.some((r) => r.name.toLowerCase() === name.trim().toLowerCase())) {
      toast.error("Ce rôle existe déjà.");
      return;
    }
    addRole(name, color);
    setName("");
    setColor(SAGE_PALETTE[data.roles.length % SAGE_PALETTE.length]);
    toast.success("Rôle ajouté.");
  };

  const count = (id: string) => data.projects.filter((p) => p.roleId === id).length;

  return (
    <div>
      <PageHeader
        overline="Domaines de vie"
        title="Quels sont tes rôles ?"
        right={
          <button
            onClick={() => navigate("/projects")}
            data-testid="roles-back"
            aria-label="Retour"
            className="h-11 w-11 rounded-full bg-white border border-forest-900/10 flex items-center justify-center text-forest-700"
          >
            <ArrowLeft strokeWidth={1.75} className="h-5 w-5" />
          </button>
        }
      />

      <Card className="p-5 mb-8">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          data-testid="role-name-input"
          placeholder="Ex. Santé, Famille, Création…"
          className="w-full bg-cream-surface border border-forest-900/5 rounded-xl px-4 py-3 text-forest-900 placeholder:text-forest-500/50 focus:outline-none focus:ring-2 focus:ring-sage/40 mb-4"
        />
        <p className="text-xs uppercase tracking-[0.18em] text-forest-500 mb-3">Couleur</p>
        <div className="flex flex-wrap items-center gap-2.5 mb-5">
          {SAGE_PALETTE.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              data-testid={`role-color-${c}`}
              aria-label={`Couleur ${c}`}
              className={`h-8 w-8 rounded-full transition-transform ${
                color === c ? "ring-2 ring-offset-2 ring-forest-800/30 scale-110" : ""
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={create}
          disabled={!name.trim()}
          data-testid="save-role"
          className="w-full rounded-full bg-sage text-white py-3 font-medium disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" /> Ajouter le rôle
        </motion.button>
      </Card>

      {data.roles.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Layers strokeWidth={1.5} className="h-7 w-7" />}
            title="Aucun rôle défini"
            hint="Les rôles t'aident à répartir tes projets par domaine de vie."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {data.roles.map((r, i) => (
            <Card key={r.id} className="p-4" data-testid={`role-${r.id}`}>
              <div className="flex items-center gap-4">
                <div className="flex flex-col -my-1 shrink-0">
                  <button
                    onClick={() => moveRole(r.id, "up")}
                    disabled={i === 0}
                    data-testid={`move-role-up-${r.id}`}
                    aria-label="Monter le rôle"
                    className="h-6 w-6 flex items-center justify-center rounded-lg text-forest-500 hover:bg-forest-900/5 disabled:opacity-25 disabled:hover:bg-transparent"
                  >
                    <ChevronUp className="h-4 w-4" strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => moveRole(r.id, "down")}
                    disabled={i === data.roles.length - 1}
                    data-testid={`move-role-down-${r.id}`}
                    aria-label="Descendre le rôle"
                    className="h-6 w-6 flex items-center justify-center rounded-lg text-forest-500 hover:bg-forest-900/5 disabled:opacity-25 disabled:hover:bg-transparent"
                  >
                    <ChevronDown className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
                <button
                  onClick={() => setColorEditId(colorEditId === r.id ? null : r.id)}
                  data-testid={`edit-role-color-${r.id}`}
                  aria-label="Modifier la couleur"
                  className="relative h-10 w-10 rounded-full shrink-0 transition-transform active:scale-95"
                  style={{ backgroundColor: r.color }}
                >
                  <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white border border-forest-900/10 flex items-center justify-center">
                    <Palette className="h-2.5 w-2.5 text-forest-500" strokeWidth={2} />
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-forest-900 truncate">{r.name}</p>
                  <p className="text-xs text-forest-500 mt-0.5">
                    {count(r.id)} projet{count(r.id) > 1 ? "s" : ""}
                  </p>
                </div>
                <button
                  onClick={() => {
                    removeRole(r.id);
                    toast("Rôle supprimé.");
                  }}
                  data-testid={`delete-role-${r.id}`}
                  aria-label="Supprimer le rôle"
                  className="p-2 text-forest-500/60 hover:text-blush"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {colorEditId === r.id && (
                <div
                  className="mt-4 pt-4 border-t border-forest-900/5 flex flex-wrap gap-2.5"
                  data-testid={`role-color-palette-${r.id}`}
                >
                  {SAGE_PALETTE.map((c) => (
                    <button
                      key={c}
                      onClick={() => {
                        setRoleColor(r.id, c);
                        setColorEditId(null);
                        toast.success("Couleur mise à jour.");
                      }}
                      data-testid={`role-color-option-${r.id}-${c}`}
                      aria-label={`Choisir ${c}`}
                      className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${
                        r.color === c ? "ring-2 ring-offset-2 ring-forest-800/30 scale-110" : ""
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Roles;
